import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { ArrowLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { getSettings, getNewsBySlug, getBrandBySlug, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import NotFound from "@/pages/NotFound";

export default function NewsDetail() {
  const { slug } = useParams();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const { data: article, isLoading, isError } = useQuery({ queryKey: ["news", slug], queryFn: () => getNewsBySlug(slug), retry: false });
  const { data: related } = useQuery({
    queryKey: ["brand", article?.relatedBrandSlug],
    queryFn: () => getBrandBySlug(article.relatedBrandSlug),
    enabled: !!article?.relatedBrandSlug,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="container-inst py-24">
        <div className="mx-auto h-72 max-w-3xl animate-pulse rounded-[14px]" style={{ background: "var(--surface)", border: "1px solid var(--card-border)" }} />
      </div>
    );
  }
  if (isError || !article) return <NotFound />;

  const cover = article.coverMediaId ? mediaUrl(article.coverMediaId) : (article.coverUrl || null);
  const date = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : "";
  const meta = [date, article.category].filter(Boolean).join("  \u00b7  ");
  const cleanHtml = DOMPurify.sanitize(article.content || "", { ADD_ATTR: ["target", "rel"] });

  return (
    <>
      <Seo
        title={article.seoTitle || article.title}
        path={`/news/${article.slug}`}
        settings={settings}
        description={article.seoDescription || article.excerpt}
        image={article.ogImage ? mediaUrl(article.ogImage) : (cover || undefined)}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "News", path: "/news" }, { name: article.title, path: `/news/${article.slug}` }]}
        article={{
          headline: article.title,
          description: article.seoDescription || article.excerpt,
          image: article.ogImage ? mediaUrl(article.ogImage) : cover,
          datePublished: article.publishedAt,
          dateModified: article.updatedAt || article.publishedAt,
        }}
      />

      {/* Masthead */}
      <section className="hero-gradient border-b" style={{ borderColor: "var(--card-border)" }}>
        <div className="container-inst pt-12 pb-14 lg:pt-16 lg:pb-20">
          <nav aria-label="Breadcrumb" className="mb-8 flex flex-wrap items-center gap-2 text-[12px]" style={{ color: "var(--muted)" }}>
            <Link to="/" className="transition-colors hover:text-[var(--text)]">Home</Link>
            <ChevronRight size={13} style={{ color: "var(--fg-subtle)" }} />
            <Link to="/news" className="transition-colors hover:text-[var(--text)]">News</Link>
            <ChevronRight size={13} style={{ color: "var(--fg-subtle)" }} />
            <span style={{ color: "var(--text)" }}>{article.title}</span>
          </nav>
          <Reveal>
            <div className="mx-auto max-w-3xl">
              {meta && <p className="text-[11px] uppercase" style={{ color: "var(--fg-subtle)", letterSpacing: "0.2em" }}>{meta}</p>}
              <h1 className="mt-4 text-[30px] leading-[1.2] sm:text-[38px] lg:text-[42px]" style={{ color: "var(--text)", fontWeight: 500, letterSpacing: "0.2px" }}>{article.title}</h1>
              <div className="bronze-rule mt-6" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Body */}
      <div className="surface-cream">
        <article className="container-inst py-14 lg:py-20">
          <div className="mx-auto max-w-3xl">
            {cover && (
              <Reveal>
                <img src={cover} alt={article.title} className="mb-10 w-full rounded-[12px] object-cover" style={{ border: "1px solid var(--card-border)", aspectRatio: "16 / 9" }} loading="lazy" />
              </Reveal>
            )}
            <div className="article-content" data-testid="news-article-content" dangerouslySetInnerHTML={{ __html: cleanHtml }} />

            {related && (
              <div className="mt-12 rounded-[12px] border p-6" style={{ borderColor: "var(--card-border)", background: "var(--surface)" }}>
                <p className="text-[11px] uppercase" style={{ color: "var(--fg-subtle)", letterSpacing: "0.2em" }}>Related Company</p>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h2 className="text-[18px]" style={{ color: "var(--text)", fontWeight: 500 }}>{related.name}</h2>
                    {related.industry && <p className="mt-1 text-[12px] uppercase" style={{ color: "var(--fg-subtle)", letterSpacing: "0.16em" }}>{related.industry}</p>}
                  </div>
                  <Link to={`/companies/${related.slug}`} data-testid="news-related-company-link" className="btn btn-secondary btn-sm">
                    View Company <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            )}

            <div className="mt-12">
              <Link to="/news" className="btn btn-ghost" style={{ color: "var(--muted)" }}>
                <ArrowLeft size={15} /> All News
              </Link>
            </div>
          </div>
        </article>
      </div>
    </>
  );
}
